/**
 * Residents Table Component
 *
 * Displays the main residents list in the Residents tab.
 * Two-row grouped header with frozen first column and horizontal scroll.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ArrowLeft, ArrowRight, CalendarClock } from "lucide-react";
import { ResidentSummary } from "../../types/residents";
import { ResidentRow, ResidentRowClickData } from "./ResidentRow";
import { SortableHeader } from "../molecules/SortableHeader";
import { SortIcon } from "../atoms/SortIcon";
import { SortField, SortDirection } from "../../types/dashboard";
import {
  NEW_INSIGHT_COLUMNS,
  PRIMARY_RISK_COLUMNS,
  ADMITTED_COLUMN,
  filterVisibleColumns,
  getInsightValue,
  getRiskFactorValue,
} from "../molecules/FactorIndicatorGroup";
import { Tooltip } from "../ui/tooltip";
import { getColumnHeaderTooltip } from "./columnHeaderTooltips";
import { trackCareQueueEvent } from "../../lib/tracking";

interface ResidentsTableProps {
  residents: ResidentSummary[];
  onRowClick: (data: ResidentRowClickData) => void;
  onAddNote: (resident: ResidentSummary) => void;
  onReviewCompleted: (resident: ResidentSummary) => void;
  onHideResident: (resident: ResidentSummary) => void;
  insightsLookback: number;
  onInsightsLookbackChange: (days: number) => void;
  groupByFacility?: boolean;
  hiddenRiskFactors?: string[];
  facilityNameMap?: Record<string, string>;
  collapsedFacilities?: Set<string>;
  onCollapsedFacilitiesChange?: (collapsed: Set<string>) => void;
}

export function ResidentsTable({
  residents,
  onRowClick,
  onAddNote,
  onReviewCompleted,
  onHideResident,
  insightsLookback,
  onInsightsLookbackChange,
  groupByFacility = false,
  hiddenRiskFactors = [],
  facilityNameMap = {},
  collapsedFacilities: externalCollapsed,
  onCollapsedFacilitiesChange,
}: ResidentsTableProps) {
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Collapsed facility groups — use external state if provided, else local
  const [localCollapsed, setLocalCollapsed] = useState<Set<string>>(new Set());
  const collapsedFacilities = externalCollapsed ?? localCollapsed;
  const setCollapsedFacilities = onCollapsedFacilitiesChange ?? setLocalCollapsed;
  const visibleInsightColumns = useMemo(
    () => filterVisibleColumns(NEW_INSIGHT_COLUMNS, hiddenRiskFactors),
    [hiddenRiskFactors],
  );
  const visibleRiskColumns = useMemo(
    () => filterVisibleColumns(PRIMARY_RISK_COLUMNS, hiddenRiskFactors),
    [hiddenRiskFactors],
  );
  const isAdmittedVisible = useMemo(
    () => filterVisibleColumns([ADMITTED_COLUMN], hiddenRiskFactors).length > 0,
    [hiddenRiskFactors],
  );

  // Scroll navigation state
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons);

    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      observer.disconnect();
    };
  }, [updateScrollButtons]);

  useEffect(() => {
    requestAnimationFrame(updateScrollButtons);
  }, [residents.length, updateScrollButtons]);

  const handleScrollLeft = () => {
    scrollRef.current?.scrollBy({ left: -270, behavior: "smooth" });
  };

  const handleScrollRight = () => {
    scrollRef.current?.scrollBy({ left: 270, behavior: "smooth" });
  };

  const totalColumns =
    5 +
    (isAdmittedVisible ? 1 : 0) +
    visibleInsightColumns.length +
    visibleRiskColumns.length;

  const handleSort = (field: SortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      if (sortDirection === "asc") {
        newDirection = "desc";
        setSortDirection(newDirection);
      } else {
        // Third click: reset to default (no sort)
        setSortField(null);
        setSortDirection(null);
        newDirection = null;
      }
    } else {
      newDirection = "asc";
      setSortField(field);
      setSortDirection(newDirection);
    }
    if (newDirection) {
      trackCareQueueEvent("care_queue_sort_changed", {
        column: field,
        direction: newDirection,
        table: "residents",
      });
    }
  };

  const sortedResidents = [...residents].sort((a, b) => {
    if (!sortField || !sortDirection) {
      const aDays = a.days_since_last_review ?? -1;
      const bDays = b.days_since_last_review ?? -1;
      return bDays - aDays;
    }

    let comparison = 0;
    if (sortField === "residentName") {
      comparison = a.resident_name.localeCompare(b.resident_name);
    } else if (sortField === "daysSinceLastReview") {
      const aDays = a.days_since_last_review ?? -1;
      const bDays = b.days_since_last_review ?? -1;
      comparison = aDays - bDays;
    } else if (sortField === "riskLevel") {
      const order: Record<string, number> = {
        HIGH: 3,
        MEDIUM: 2,
        LOW: 1,
        UNKNOWN: 0,
      };
      comparison = (order[a.risk_level] ?? 0) - (order[b.risk_level] ?? 0);
    } else {
      // Boolean indicator columns (New Insights + Primary Risk Factors)
      const insightKeys = NEW_INSIGHT_COLUMNS.map((c) => c.key);
      const riskKeys = PRIMARY_RISK_COLUMNS.map((c) => c.key);

      if (insightKeys.includes(sortField)) {
        const aVal = getInsightValue(a.new_insights, sortField);
        const bVal = getInsightValue(b.new_insights, sortField);
        // true=1, false=0, null=-1 (N/A sorts last)
        const toNum = (v: boolean | null) =>
          v === true ? 1 : v === false ? 0 : -1;
        comparison = toNum(aVal) - toNum(bVal);
      } else if (
        riskKeys.includes(sortField) ||
        sortField === ADMITTED_COLUMN.key
      ) {
        const aVal = getRiskFactorValue(a.primary_risk_factors, sortField);
        const bVal = getRiskFactorValue(b.primary_risk_factors, sortField);
        // true=1, false=0, null=-1 (N/A sorts last)
        const toNum = (v: boolean | null) =>
          v === true ? 1 : v === false ? 0 : -1;
        comparison = toNum(aVal) - toNum(bVal);
      }
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div className="border-2 border-solid border-border rounded-[var(--radius)] overflow-hidden flex-1 min-h-0 flex flex-col">
        <div ref={scrollRef} className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full border-separate border-spacing-0">
            {/* Two-row grouped header */}
            <thead className="sticky top-0 z-10">
              {/* Row 1: group headers */}
              <tr className="bg-muted">
                {/* Action menu column */}
                <th
                  rowSpan={2}
                  className="w-[48px] bg-muted p-0 border-b-2 border-border"
                />

                {/* Frozen: Resident Info */}
                <th
                  rowSpan={2}
                  className="sticky left-0 z-30 bg-muted min-w-[220px] p-1 text-left border-r border-b-2 border-border"
                >
                  <div className="flex items-center">
                    <SortableHeader
                      label="Resident Info"
                      field={"residentName" as SortField}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                  </div>
                </th>

                {/* RISK LEVEL group — sortable */}
                <th
                  colSpan={2}
                  className="p-2 text-center border-l border-b border-border"
                >
                  <button
                    type="button"
                    onClick={() => handleSort("riskLevel" as SortField)}
                    className="inline-flex items-center gap-1 cursor-pointer bg-transparent border-none p-0 hover:opacity-70 transition-opacity"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Risk Score
                    </span>
                    <SortIcon
                      field={"riskLevel" as SortField}
                      sortField={sortField}
                      sortDirection={sortDirection}
                    />
                  </button>
                </th>

                {/* ADMITTED < 30 DAYS — standalone column */}
                {isAdmittedVisible && (
                  <th
                    rowSpan={2}
                    className="min-w-[90px] p-2 text-center border-l border-b-2 border-border"
                  >
                    <SortableHeader
                      label={ADMITTED_COLUMN.label}
                      field={ADMITTED_COLUMN.key as SortField}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      icon={
                        <CalendarClock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      }
                    />
                  </th>
                )}

                {/* NEW INSIGHTS group */}
                <th
                  colSpan={visibleInsightColumns.length}
                  className="p-2 text-center border-l border-b border-border"
                >
                  <div className="inline-flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      New Insights
                    </span>
                    <select
                      value={insightsLookback}
                      onChange={(e) =>
                        onInsightsLookbackChange(Number(e.target.value))
                      }
                      className="appearance-none pl-2 pr-5 py-0.5 border border-solid border-[#228189]/30 rounded-full text-xs font-semibold text-[#228189] bg-[#228189]/5 cursor-pointer hover:bg-[#228189]/10 transition-colors focus:outline-none focus:ring-1 focus:ring-[#228189]/40"
                    >
                      {[3, 5, 7, 14, 21, 30].map((d) => (
                        <option key={d} value={d}>
                          Last {d} Days
                        </option>
                      ))}
                    </select>
                  </div>
                </th>

                {/* PRIMARY RISK FACTORS group */}
                <th
                  colSpan={visibleRiskColumns.length}
                  className="p-2 text-center border-l border-b border-border"
                >
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Other Risk Factors
                  </span>
                </th>
              </tr>

              {/* Row 2: sub-column headers */}
              <tr className="bg-muted">
                {/* Risk Level group sub-headers */}
                <th className="min-w-[70px] p-2 text-center border-b-2 border-border border-l">
                  <Tooltip
                    content={getColumnHeaderTooltip("notes")}
                    placement="top"
                    offset={4}
                  >
                    <span className="text-[11px] font-medium text-muted-foreground leading-tight whitespace-nowrap cursor-help">
                      Notes
                    </span>
                  </Tooltip>
                </th>
                <th className="min-w-[100px] p-2 text-center border-b-2 border-border">
                  <Tooltip
                    content={getColumnHeaderTooltip("daysSinceLastReview")}
                    placement="top"
                    offset={4}
                  >
                    <span className="inline-flex cursor-help">
                      <SortableHeader
                        label="Days Since Last Review"
                        field={"daysSinceLastReview" as SortField}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    </span>
                  </Tooltip>
                </th>

                {visibleInsightColumns.map((col, i) => {
                  const Icon = col.icon;
                  const tooltip = getColumnHeaderTooltip(col.key);
                  const headerNode = (
                    <SortableHeader
                      label={col.label}
                      field={col.key as SortField}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      icon={
                        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      }
                    />
                  );
                  return (
                    <th
                      key={col.key}
                      className={`min-w-[90px] p-2 text-center whitespace-nowrap text-nowrap border-b-2 border-border ${i === 0 ? "border-l" : ""}`}
                    >
                      {tooltip ? (
                        <Tooltip content={tooltip} placement="top" offset={4}>
                          <span className="inline-flex cursor-help">
                            {headerNode}
                          </span>
                        </Tooltip>
                      ) : (
                        headerNode
                      )}
                    </th>
                  );
                })}
                {visibleRiskColumns.map((col, i) => {
                  const Icon = col.icon;
                  const tooltip = getColumnHeaderTooltip(col.key);
                  const headerNode = (
                    <SortableHeader
                      label={col.label}
                      field={col.key as SortField}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      icon={
                        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      }
                    />
                  );
                  return (
                    <th
                      key={col.key}
                      className={`min-w-[90px] p-2 text-center border-b-2 border-border ${i === 0 ? "border-l" : ""}`}
                    >
                      {tooltip ? (
                        <Tooltip content={tooltip} placement="top" offset={4}>
                          <span className="inline-flex cursor-help">
                            {headerNode}
                          </span>
                        </Tooltip>
                      ) : (
                        headerNode
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {sortedResidents.length === 0 ? (
                <tr>
                  <td
                    colSpan={totalColumns}
                    className="p-8 text-center text-muted-foreground"
                  >
                    No Residents found
                  </td>
                </tr>
              ) : (
                (() => {
                  if (!groupByFacility) {
                    return sortedResidents.map((resident, index) => (
                      <ResidentRow
                        key={resident.resident_id || index}
                        resident={resident}
                        index={index}
                        isLastRow={index === sortedResidents.length - 1}
                        onClick={onRowClick}
                        onAddNote={onAddNote}
                        onReviewCompleted={onReviewCompleted}
                        onHideResident={onHideResident}
                        insightsLookback={insightsLookback}
                        visibleInsightColumns={visibleInsightColumns}
                        visibleRiskColumns={visibleRiskColumns}
                        isAdmittedVisible={isAdmittedVisible}
                      />
                    ));
                  }
                  const groups: {
                    facilityId: string;
                    facilityName: string;
                    rows: typeof sortedResidents;
                  }[] = [];
                  for (const r of sortedResidents) {
                    const fid = r.facility_id || "unknown";
                    const fname =
                      facilityNameMap[fid] ||
                      r.facility_name ||
                      fid ||
                      "Unknown Facility";
                    const existing = groups.find((g) => g.facilityId === fid);
                    if (existing) {
                      existing.rows.push(r);
                    } else {
                      groups.push({
                        facilityId: fid,
                        facilityName: fname,
                        rows: [r],
                      });
                    }
                  }
                  return groups.flatMap(
                    ({ facilityId, facilityName, rows }) => {
                      const collapsed = collapsedFacilities.has(facilityId);
                      return [
                        <tr
                          key={`facility-${facilityId}`}
                          className="bg-muted/60 border-t-2 border-border cursor-pointer select-none hover:bg-muted/80"
                          onClick={() => {
                            const wasCollapsed = collapsedFacilities.has(facilityId);
                            trackCareQueueEvent("care_queue_facility_group_toggled", {
                              action: wasCollapsed ? "expand" : "collapse",
                              table: "residents",
                            });
                            const next = new Set(collapsedFacilities);
                            if (next.has(facilityId)) next.delete(facilityId);
                            else next.add(facilityId);
                            setCollapsedFacilities(next);
                          }}
                        >
                          <td
                            colSpan={totalColumns}
                            className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                          >
                            <span className="mr-2">
                              {collapsed ? "▶" : "▼"}
                            </span>
                            {facilityName}{" "}
                            <span className="font-normal">({rows.length})</span>
                          </td>
                        </tr>,
                        ...rows.map((resident, index) => (
                              <ResidentRow
                                key={
                                  resident.resident_id ||
                                  `${facilityId}-${index}`
                                }
                                resident={resident}
                                index={index}
                                isLastRow={!collapsed && index === rows.length - 1}
                                onClick={onRowClick}
                                onAddNote={onAddNote}
                                onReviewCompleted={onReviewCompleted}
                                onHideResident={onHideResident}
                                insightsLookback={insightsLookback}
                                visibleInsightColumns={visibleInsightColumns}
                                visibleRiskColumns={visibleRiskColumns}
                                isAdmittedVisible={isAdmittedVisible}
                                style={collapsed ? { display: 'none' } : undefined}
                              />
                            )),
                      ];
                    },
                  );
                })()
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Left scroll indicator — gradient edge shadow + button */}
      {canScrollLeft && (
        <>
          <div
            className="absolute left-[220px] top-0 bottom-0 w-[45px] z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(to left, transparent, rgba(0, 0, 0, 0.30))",
            }}
          />
          <button
            type="button"
            onClick={handleScrollLeft}
            className="absolute left-[220px] top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            style={{
              background: "#F7F7F7",
              boxShadow:
                "0px 1px 18px rgba(0, 0, 0, 0.12), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)",
            }}
            aria-label="Scroll left"
          >
            <ArrowLeft className="h-5 w-5 text-[#228189]" />
          </button>
        </>
      )}

      {/* Right scroll indicator — gradient edge shadow + button */}
      {canScrollRight && (
        <>
          <div
            className="absolute right-0 top-0 bottom-0 w-[45px] z-10 pointer-events-none"
            style={{
              background:
                "linear-gradient(to right, transparent, rgba(0, 0, 0, 0.30))",
            }}
          />
          <button
            type="button"
            onClick={handleScrollRight}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-colors cursor-pointer"
            style={{
              background: "#F7F7F7",
              boxShadow:
                "0px 1px 18px rgba(0, 0, 0, 0.12), 0px 6px 10px rgba(0, 0, 0, 0.14), 0px 3px 5px -1px rgba(0, 0, 0, 0.20)",
            }}
            aria-label="Scroll right"
          >
            <ArrowRight className="h-5 w-5 text-[#228189]" />
          </button>
        </>
      )}
    </div>
  );
}
